import dayjs from "dayjs"
import { type FC } from "react"
import { useTheme } from "styled-components"
import Values from "values.js"

import { toNumberFormat } from "@forento/shared/utilities/number"

import { usePlatform } from "~/contexts/PlatformContext"
import { useUser } from "~/contexts/UserContext"
import useGreeting from "~/hooks/useGreeting"
import useMonthNavigation from "~/hooks/useMonthNavigation"
import Button from "~/themes/school/components/Button"
import { ChevronIcon, ClockIcon } from "~/themes/school/components/Icon"
import Layout from "~/themes/school/components/Layout"
import { PageTitle } from "~/themes/school/components/PageHeader"
import ActiveCourses from "~/themes/school/pages/dashboard/Dashboard/ActiveCourses"
import { useTranslation } from "~/translations"
import { isColorDark } from "~/utilities/color"
import { cn } from "~/utilities/tailwind"
import { query } from "~/utilities/trpc"

const DashboardPage: FC = () => {
	const platform = usePlatform().platform
	const user = useUser().user
	const t = useTranslation()
	const greeting = useGreeting()
	const theme = useTheme()

	const calendar = useMonthNavigation()

	const events = query.event.listPublic.useQuery()
	const communityProfile = query.community.getProfile.useQuery(user?.communityProfileId ?? -1, {
		enabled: platform.community?.isGamificationEnabled ?? false,
	})

	const firstDayOfMonth = dayjs(`${calendar.year}-${calendar.month + 1}-01`)
	const daysInMonth = firstDayOfMonth.daysInMonth()
	// Convert Sunday (0) to 6, and other days to 0-5, effectively making Monday (1) the first day
	const startDayOfWeek = (firstDayOfMonth.day() + 6) % 7

	const prevMonth = firstDayOfMonth.subtract(1, "month")
	const daysInPrevMonth = prevMonth.daysInMonth()

	const weekDays = [
		t("dashboard.weekDays.monday"),
		t("dashboard.weekDays.tuesday"),
		t("dashboard.weekDays.wednesday"),
		t("dashboard.weekDays.thursday"),
		t("dashboard.weekDays.friday"),
		t("dashboard.weekDays.saturday"),
		t("dashboard.weekDays.sunday"),
	]

	return (
		<Layout>
			<PageTitle>{greeting ?? t("dashboard.title")}</PageTitle>
			<div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,400px)_minmax(0,400px)]">
				{platform.content.courses && <ActiveCourses className="row-span-2" />}
				<div className="bg-white p-6 shadow">
					<div className="mb-6 flex items-center justify-between">
						<Button
							onClick={calendar.setPrevious}
							isDisabled={calendar.year === dayjs().year() && calendar.month === dayjs().month()}
							className="disabled:opacity-25"
						>
							<ChevronIcon direction="left" className="h-6 w-6" />
						</Button>
						<h2 className="font-quicksand font-bold">
							{dayjs(`${calendar.year}-${calendar.month + 1}-01`).format("MMMM YYYY")}
						</h2>
						<Button onClick={calendar.setNext}>
							<ChevronIcon direction="right" className="h-6 w-6" />
						</Button>
					</div>
					<div className="grid grid-cols-7 grid-rows-5 gap-4">
						{weekDays.map(day => (
							<div key={day} className="text-light text-center">
								{day}
							</div>
						))}
						{Array.from({ length: 7 * 5 }).map((_, index) => {
							let date: number
							let monthOffset = 0

							if (index < startDayOfWeek) {
								date = daysInPrevMonth - startDayOfWeek + index + 1
								monthOffset = -1
							} else if (index >= startDayOfWeek + daysInMonth) {
								date = index - (startDayOfWeek + daysInMonth) + 1
								monthOffset = 1
							} else {
								date = index - startDayOfWeek + 1
							}

							const eventsThisDate =
								events.data?.filter(x => {
									const days = Array.from(
										{
											length:
												dayjs(x.endDate).subtract(1, "second").diff(dayjs(x.startDate), "day") +
												1,
										},
										(_, i) => dayjs(x.startDate).add(i, "day"),
									)
									const currentDate = dayjs(
										new Date(calendar.year, calendar.month + monthOffset, date),
									)
									return days.some(x => x.isSame(currentDate, "day"))
								}) ?? []

							const isToday =
								dayjs().month() === calendar.month &&
								dayjs().year() === calendar.year &&
								dayjs().date() === date

							return (
								<div
									key={`date-${index}`}
									className={cn(
										"group relative flex aspect-square items-center justify-center rounded-full",
										monthOffset === 0 ? "font-bold" : "text-light",
										isToday && "text-red-500",
										eventsThisDate?.length > 0 &&
											cn(
												"bg-primary cursor-pointer font-bold",
												isToday
													? "bg-red-500 text-white"
													: isColorDark(theme.primaryColor) && "text-white",
											),
									)}
								>
									{date}
									{eventsThisDate.length > 0 && (
										<div className="pointer-events-none absolute bottom-full left-1/2 mb-3 flex min-w-[200px] origin-bottom -translate-x-1/2 -translate-y-1 scale-0 flex-col gap-3 rounded-xl bg-gray-800 px-4 py-3 text-sm text-white shadow-xl transition-all duration-150 group-hover:scale-100">
											{eventsThisDate.map(event => {
												const isMultiDayEvent = !dayjs(event.startDate).isSame(
													dayjs(event.endDate).subtract(1, "second"),
													"day",
												)
												return (
													<div key={event.id}>
														<div className="text-base font-medium">{event.title}</div>
														<div className="mt-2 flex items-center gap-2 text-gray-300">
															<ClockIcon className="h-4 w-4" />
															{isMultiDayEvent ? (
																<div className="flex flex-col">
																	<div>
																		{dayjs(event.startDate).format("MMM D HH:mm")}
																	</div>
																	<div className="flex items-center gap-1">
																		<span className="text-gray-500">to</span>
																		{dayjs(event.endDate).format("MMM D HH:mm")}
																	</div>
																</div>
															) : (
																<>
																	{dayjs(event.startDate).format("HH:mm")}&mdash;
																	{dayjs(event.endDate).format("HH:mm")}
																</>
															)}
														</div>
													</div>
												)
											})}
										</div>
									)}
								</div>
							)
						})}
					</div>
				</div>
				{communityProfile.data?.gamification && (
					<div className="flex flex-col items-center bg-white p-6 shadow">
						<h2 className="font-quicksand mb-6 self-start font-bold">{t("dashboard.communityPoints")}</h2>
						<div className="mb-6 flex flex-col items-center">
							<div className="text-light font-quicksand text-xl font-bold">
								<span className="text-primary">
									{toNumberFormat(communityProfile.data.gamification.points)}
								</span>
								{communityProfile.data.gamification.nextLevel && (
									<> / {toNumberFormat(communityProfile.data.gamification.nextLevel.unlockPoints)}</>
								)}
							</div>
							{communityProfile.data.gamification.nextLevel && (
								<div className="text-light mt-1 text-center">
									{t("dashboard.nextLevel", {
										level: communityProfile.data.gamification.nextLevel.label,
									})}
								</div>
							)}
						</div>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="127"
							height="127"
							viewBox="0 0 127 127"
							fill="none"
							className="text-pretty"
						>
							<circle
								cx="63.5"
								cy="63.5"
								r="63.5"
								fill={new Values(theme.primaryColor).tint(75).hexString()}
							/>
							<path
								d="M65.8328 45.0819C65.8328 45.0819 65.1914 45.2423 64.2293 45.4026C64.8707 47.1665 65.1914 49.4114 65.3517 51.8167C67.5967 49.0907 72.0865 48.77 73.69 49.8925C73.69 49.8925 72.0865 55.8255 65.5121 54.8634C65.5121 56.7876 65.3517 58.7119 65.031 60.3154H61.6636C62.4654 56.1462 62.4654 47.9683 60.3808 44.9216C50.439 42.3559 49.4769 33.0555 49.4769 33.0555C57.3341 32.4141 60.2205 35.1401 61.9843 40.111C62.9464 27.2828 75.4539 26 75.4539 26C78.5006 35.9418 73.5297 42.3559 65.8328 45.0819Z"
								fill={theme.primaryColor}
							/>
							<path
								d="M47.5527 76.1903L51.5615 99.4413C51.7218 100.083 52.2029 100.564 52.8443 100.564H73.0487C73.6901 100.564 74.3315 100.083 74.4919 99.4413L78.9817 76.1903C79.1421 75.5488 78.661 75.0678 78.0196 75.0678H48.5148C47.8734 74.9074 47.3923 75.5488 47.5527 76.1903Z"
								fill={theme.primaryColor}
							/>
							<path
								d="M45.1474 72.1815H81.387C82.0284 72.1815 82.5095 71.7004 82.5095 71.059V63.8431C82.5095 63.2017 82.0284 62.7207 81.387 62.7207H45.1474C44.506 62.7207 44.0249 63.2017 44.0249 63.8431V71.059C43.8646 71.7004 44.506 72.1815 45.1474 72.1815Z"
								fill={theme.primaryColor}
							/>
						</svg>
					</div>
				)}
			</div>
		</Layout>
	)
}

export default DashboardPage
